import type { HTMLAttributes } from 'react';

type BadgeColor   = 'default' | 'primary' | 'secondary' | 'accent' | 'success' | 'warning' | 'danger' | 'info';
type BadgeVariant = 'solid' | 'soft' | 'outline';
type BadgeSize    = 'sm' | 'md';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  color?:   BadgeColor;
  variant?: BadgeVariant;
  size?:    BadgeSize;
  dot?:     boolean;
}

const colorMap: Record<BadgeColor, Record<BadgeVariant, string>> = {
  default:   {
    solid:   'bg-neutral-700 text-neutral-100',
    soft:    'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
    outline: 'border border-[var(--border)] text-[var(--muted)]',
  },
  primary:   {
    solid:   'bg-primary-500 text-neutral-900',
    soft:    'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    outline: 'border border-primary-500 text-primary-600 dark:text-primary-400',
  },
  secondary: {
    solid:   'bg-secondary-400 text-neutral-900',
    soft:    'bg-secondary-50 text-secondary-700 dark:bg-secondary-900/30 dark:text-secondary-300',
    outline: 'border border-secondary-500 text-secondary-600 dark:text-secondary-400',
  },
  accent:    {
    solid:   'bg-accent-500 text-white',
    soft:    'bg-accent-50 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300',
    outline: 'border border-accent-500 text-accent-600 dark:text-accent-400',
  },
  success:   {
    solid:   'bg-success-500 text-white',
    soft:    'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    outline: 'border border-success-500 text-success-600',
  },
  warning:   {
    solid:   'bg-warning-500 text-neutral-900',
    soft:    'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    outline: 'border border-warning-500 text-warning-600',
  },
  danger:    {
    solid:   'bg-danger-500 text-white',
    soft:    'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    outline: 'border border-danger-500 text-danger-600',
  },
  info:      {
    solid:   'bg-info-500 text-white',
    soft:    'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    outline: 'border border-info-500 text-info-600',
  },
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'text-xs px-2 py-0.5 gap-1',
  md: 'text-xs px-2.5 py-1 gap-1.5',
};

export function Badge({
  color   = 'default',
  variant = 'soft',
  size    = 'sm',
  dot     = false,
  className = '',
  children,
  ...props
}: BadgeProps) {
  return (
    <span
      className={[
        'inline-flex items-center rounded-full font-medium',
        colorMap[color][variant],
        sizeClasses[size],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      {dot && (
        <span
          className={[
            'w-1.5 h-1.5 rounded-full shrink-0',
            variant === 'solid'
              ? 'bg-current opacity-70'
              : color === 'primary' ? 'bg-primary-500' : 'bg-current opacity-60',
          ].join(' ')}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}

export default Badge;
